import { Resource } from '@opentelemetry/resources';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { trace, SpanStatusCode } from '@opentelemetry/api';

// Interface untuk konfigurasi SignOz
export interface SignOzConfig {
  serviceName: string;
  serviceVersion: string;
  environment: string;
  serviceNamespace: string;
  url: string;
  headers?: Record<string, string>;
  traceSampleRate?: number;
  batchSpanProcessorConfig?: {
    maxQueueSize?: number;
    scheduledDelayMillis?: number;
    exportTimeoutMillis?: number;
    maxExportBatchSize?: number;
  };
  allowedOrigins?: (string | RegExp)[];
  enableRequestLogging?: boolean; // Enable logging untuk request/response data
  logRequestBody?: boolean; // Log request body untuk POST/PUT/PATCH
  logResponseBody?: boolean; // Log response body untuk GET dan lainnya
  maxBodyLogSize?: number; // Maksimal ukuran body yang di-log (dalam bytes)
  enableDocumentLoad?: boolean; // Enable document load instrumentation (default: false)
  enableErrorTracking?: boolean; // Enable automatic error tracking (default: true)
  enableNavigationTracking?: boolean; // Enable navigation/route tracking (default: false)
  enableConsoleLog?: boolean; // Enable console.log output (default: false)
  enableWebSocketLogging?: boolean; // Enable WebSocket logging (default: false)
  logWebSocketMessages?: boolean; // Log WebSocket messages (default: false)
}

// Interface untuk window object dengan environment variables
declare global {
  interface Window {
    REACT_APP_SIGNOZ_SERVICE_NAME: string;
    REACT_APP_SIGNOZ_SERVICE_VERSION: string;
    REACT_APP_SIGNOZ_ENV: string;
    REACT_APP_SIGNOZ_SERVICE_NAMESPACE: string;
    REACT_APP_SIGNOZ_URL: string;
    REACT_APP_SIGNOZ_TRACE_SAMPLE_RATE: string;
    REACT_APP_SIGNOZ_ALLOWED_ORIGINS: string;
  }
}

// Fungsi untuk mendapatkan nilai konfigurasi dari process.env atau window
function getConfigValue(key: string): string | undefined {
  // Cek process.env terlebih dahulu
  if (typeof process !== 'undefined' && process.env) {
    const envValue = process.env[key];
    if (envValue) return envValue;
  }

  // Fallback ke window jika process.env kosong
  if (typeof window !== 'undefined') {
    return (window as any)[key];
  }

  return undefined;
}

// Fungsi untuk memvalidasi konfigurasi
function validateConfig(config: Partial<SignOzConfig>): { isValid: boolean; missingFields: string[] } {
  const requiredFields = ['serviceName', 'serviceVersion', 'environment', 'serviceNamespace', 'url'];
  const missingFields: string[] = [];

  for (const field of requiredFields) {
    if (!config[field as keyof SignOzConfig]) {
      missingFields.push(field);
    }
  }

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
}

// Fungsi untuk mengkonversi string allowed origins ke array
function parseAllowedOrigins(originsStr?: string): (string | RegExp)[] {
  // Jika tidak ada origins yang diset, izinkan semua URL
  if (!originsStr) return ['*'];

  return originsStr.split(',').map(origin => {
    origin = origin.trim();
    // Jika string dimulai dan diakhiri dengan '/', anggap sebagai RegExp
    if (origin.startsWith('/') && origin.endsWith('/')) {
      return new RegExp(origin.slice(1, -1));
    }
    // Jika origin adalah '*', itu berarti allow all
    if (origin === '*') {
      return '.*';
    }
    return origin;
  });
}

// Fungsi helper untuk truncate body jika terlalu besar
function truncateBody(body: any, maxSize: number): string {
  try {
    const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
    if (bodyStr.length > maxSize) {
      return bodyStr.substring(0, maxSize) + '... [truncated]';
    }
    return bodyStr;
  } catch (e) {
    return '[Unable to serialize body]';
  }
}

// Fungsi untuk menambahkan logging ke fetch instrumentation
function addFetchLogging(config: {
  enableRequestLogging: boolean;
  logRequestBody: boolean;
  logResponseBody: boolean;
  maxBodyLogSize: number;
  enableConsoleLog: boolean;
}) {
  if (!config.enableRequestLogging) return;

  const originalFetch = window.fetch;
  window.fetch = async function(...args: Parameters<typeof fetch>): Promise<Response> {
    const [resource, init] = args;
    const url = typeof resource === 'string' ? resource : (resource instanceof Request ? resource.url : resource.toString());
    const method = init?.method || 'GET';
    
    // Capture current page info
    const pageUrl = window.location.href;
    const pagePath = window.location.pathname;
    
    // Track start time
    const startTime = performance.now();
    
    const tracer = trace.getTracer('fetch-logger');
    const span = tracer.startSpan(`HTTP ${method} ${url}`);
    
    try {
      // Log request data
      span.setAttribute('http.url', url);
      span.setAttribute('http.method', method);
      
      // Log page info (frontend URL)
      span.setAttribute('page.url', pageUrl);
      span.setAttribute('page.pathname', pagePath);
      span.setAttribute('page.search', window.location.search);
      span.setAttribute('page.hash', window.location.hash);
      
      if (config.logRequestBody && init?.body && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
        const bodyStr = truncateBody(init.body, config.maxBodyLogSize);
        span.setAttribute('http.request.body', bodyStr);
        if (config.enableConsoleLog) {
          console.log(`[SignOz] ${method} Request to ${url} from page ${pagePath}:`, bodyStr);
        }
      }
      
      const response = await originalFetch.apply(this, args);
      
      // Calculate duration
      const duration = performance.now() - startTime;
      span.setAttribute('duration_ms', Math.round(duration));
      
      // Log response data
      span.setAttribute('http.status_code', response.status);
      
      // Log response content length from headers
      const contentLength = response.headers.get('content-length');
      if (contentLength) {
        span.setAttribute('http.response_content_length', parseInt(contentLength));
      }
      
      // Log response body for both success and error responses
      if (config.logResponseBody) {
        const clonedResponse = response.clone();
        try {
          const responseData = await clonedResponse.text();
          const truncatedData = truncateBody(responseData, config.maxBodyLogSize);
          span.setAttribute('response.data', truncatedData);
          
          // If content-length header is not available, calculate from response data
          if (!contentLength) {
            span.setAttribute('http.response_content_length', responseData.length);
          }
          
          // Try to parse JSON and extract message if exists
          try {
            const jsonData = JSON.parse(responseData);
            if (jsonData.message) {
              span.setAttribute('response.message', jsonData.message);
            }
            // Also log error details if present
            if (jsonData.errors) {
              span.setAttribute('response.errors', JSON.stringify(jsonData.errors));
            }
            if (jsonData.status !== undefined) {
              span.setAttribute('response.status', jsonData.status);
            }
            if (jsonData.status_code !== undefined) {
              span.setAttribute('response.status_code', jsonData.status_code);
            }
          } catch (e) {
            // Not JSON or no message field, ignore
          }
          
          if (config.enableConsoleLog) {
            if (response.ok) {
              console.log(`[SignOz] ${method} Response from ${url} (${response.status}) on page ${pagePath} [${Math.round(duration)}ms]:`, truncatedData);
            } else {
              console.error(`[SignOz] ${method} Error Response from ${url} (${response.status}) on page ${pagePath} [${Math.round(duration)}ms]:`, truncatedData);
            }
          }
        } catch (e) {
          if (config.enableConsoleLog) {
            console.warn('[SignOz] Failed to read response body:', e);
          }
        }
      } else if (!response.ok && config.enableConsoleLog) {
        console.error(`[SignOz] ${method} Request to ${url} from page ${pagePath} - Status: ${response.status} [${Math.round(duration)}ms]`);
      }
      
      span.setStatus({ code: SpanStatusCode.OK });
      return response;
    } catch (error) {
      // Calculate duration even on error
      const duration = performance.now() - startTime;
      span.setAttribute('duration_ms', Math.round(duration));
      
      span.recordException(error as Error);
      span.setStatus({ code: SpanStatusCode.ERROR, message: (error as Error).message });
      if (config.enableConsoleLog) {
        console.error(`[SignOz] ${method} Error for ${url} on page ${pagePath} [${Math.round(duration)}ms]:`, error);
      }
      throw error;
    } finally {
      span.end();
    }
  };
}

// Fungsi untuk menambahkan logging ke XMLHttpRequest instrumentation
function addXHRLogging(config: {
  enableRequestLogging: boolean;
  logRequestBody: boolean;
  logResponseBody: boolean;
  maxBodyLogSize: number;
  enableConsoleLog: boolean;
}) {
  if (!config.enableRequestLogging) return;

  const OriginalXHR = window.XMLHttpRequest;
  
  window.XMLHttpRequest = function() {
    const xhr = new OriginalXHR();
    let method = '';
    let url = '';
    let startTime = 0;
    
    // Capture current page info when XHR is created
    const pageUrl = window.location.href;
    const pagePath = window.location.pathname;
    
    const originalOpen = xhr.open;
    xhr.open = function(this: XMLHttpRequest, ...args: any[]) {
      method = args[0];
      url = args[1];
      return originalOpen.apply(this, args as any);
    };
    
    const originalSend = xhr.send;
    xhr.send = function(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      // Track start time
      startTime = performance.now();
      
      const tracer = trace.getTracer('xhr-logger');
      const span = tracer.startSpan(`HTTP ${method} ${url}`);
      
      span.setAttribute('http.url', url);
      span.setAttribute('http.method', method);
      
      // Log page info (frontend URL)
      span.setAttribute('page.url', pageUrl);
      span.setAttribute('page.pathname', pagePath);
      span.setAttribute('page.search', window.location.search);
      span.setAttribute('page.hash', window.location.hash);
      
      // Log request body
      if (config.logRequestBody && body && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
        const bodyStr = truncateBody(body, config.maxBodyLogSize);
        span.setAttribute('http.request.body', bodyStr);
        if (config.enableConsoleLog) {
          console.log(`[SignOz] ${method} Request to ${url} from page ${pagePath}:`, bodyStr);
        }
      }
      
      this.addEventListener('load', function() {
        // Calculate duration
        const duration = performance.now() - startTime;
        span.setAttribute('duration_ms', Math.round(duration));
        span.setAttribute('http.status_code', xhr.status);
        
        // Log response content length from headers
        const contentLength = xhr.getResponseHeader('content-length');
        if (contentLength) {
          span.setAttribute('http.response_content_length', parseInt(contentLength));
        }
        
        // Log response body for both success and error responses
        if (config.logResponseBody) {
          try {
            // Only read responseText if responseType allows it
            if (xhr.responseType === '' || xhr.responseType === 'text') {
              const responseData = xhr.responseText;
              const truncatedData = truncateBody(responseData, config.maxBodyLogSize);
              span.setAttribute('response.data', truncatedData);
              
              // If content-length header is not available, calculate from response data
              if (!contentLength) {
                span.setAttribute('http.response_content_length', responseData.length);
              }
              
              // Try to parse JSON and extract message if exists
              try {
                const jsonData = JSON.parse(responseData);
                if (jsonData.message) {
                  span.setAttribute('response.message', jsonData.message);
                }
                // Also log error details if present
                if (jsonData.errors) {
                  span.setAttribute('response.errors', JSON.stringify(jsonData.errors));
                }
                if (jsonData.status !== undefined) {
                  span.setAttribute('response.status', jsonData.status);
                }
                if (jsonData.status_code !== undefined) {
                  span.setAttribute('response.status_code', jsonData.status_code);
                }
              } catch (e) {
                // Not JSON or no message field, ignore
              }
              
              if (config.enableConsoleLog) {
                if (xhr.status >= 200 && xhr.status < 300) {
                  console.log(`[SignOz] ${method} Response from ${url} (${xhr.status}) on page ${pagePath} [${Math.round(duration)}ms]:`, truncatedData);
                } else {
                  console.error(`[SignOz] ${method} Error Response from ${url} (${xhr.status}) on page ${pagePath} [${Math.round(duration)}ms]:`, truncatedData);
                }
              }
            } else {
              // For other response types (blob, arraybuffer, etc), just log the type
              span.setAttribute('response.data', `[${xhr.responseType} data]`);
              span.setAttribute('response.type', xhr.responseType);
              
              if (config.enableConsoleLog) {
                console.log(`[SignOz] ${method} Response from ${url} (${xhr.status}) on page ${pagePath} [${Math.round(duration)}ms]: [${xhr.responseType} data]`);
              }
            }
          } catch (e) {
            // If there's an error reading response, log it
            span.setAttribute('response.data', '[Error reading response]');
            if (config.enableConsoleLog) {
              console.warn(`[SignOz] Failed to read response from ${url}:`, e);
            }
          }
        } else if (xhr.status >= 300 && config.enableConsoleLog) {
          console.error(`[SignOz] ${method} Request to ${url} from page ${pagePath} - Status: ${xhr.status} [${Math.round(duration)}ms]`);
        }
        
        span.setStatus({ code: SpanStatusCode.OK });
        span.end();
      });
      
      this.addEventListener('error', function() {
        // Calculate duration even on error
        const duration = performance.now() - startTime;
        span.setAttribute('duration_ms', Math.round(duration));
        
        span.recordException(new Error('XHR request failed'));
        span.setStatus({ code: SpanStatusCode.ERROR, message: 'XHR request failed' });
        if (config.enableConsoleLog) {
          console.error(`[SignOz] ${method} Error for ${url} on page ${pagePath} [${Math.round(duration)}ms]`);
        }
        span.end();
      });
      
      return originalSend.call(this, body);
    };
    
    return xhr;
  } as any;
}

// Fungsi untuk menambahkan error tracking
function addErrorTracking(enableConsoleLog: boolean = false) {
  // Track unhandled errors
  window.addEventListener('error', (event) => {
    const tracer = trace.getTracer('error-tracker');
    const span = tracer.startSpan('Unhandled Error');
    
    span.setAttribute('error.type', 'unhandled');
    span.setAttribute('error.message', event.message);
    span.setAttribute('error.filename', event.filename || 'unknown');
    span.setAttribute('error.lineno', event.lineno || 0);
    span.setAttribute('error.colno', event.colno || 0);
    
    // Add page context
    span.setAttribute('page.url', window.location.href);
    span.setAttribute('page.pathname', window.location.pathname);
    span.setAttribute('page.search', window.location.search);
    span.setAttribute('page.hash', window.location.hash);
    
    if (event.error) {
      span.recordException(event.error);
      span.setAttribute('error.stack', event.error.stack || '');
      span.setAttribute('error.name', event.error.name || 'Error');
    }
    
    span.setStatus({ code: SpanStatusCode.ERROR, message: event.message });
    
    if (enableConsoleLog) {
      console.error('[SignOz] Unhandled Error:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        page: window.location.pathname
      });
    }
    
    span.end();
  }, true); // Use capture phase to catch errors early

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    // Suppress BatchSpanProcessor timeout errors to avoid noise
    const reason = String(event.reason);
    const stack = event.reason instanceof Error ? (event.reason.stack || '') : '';
    
    // Check if this is a SignOz internal timeout error
    const isSignOzTimeout = (
      reason.includes('Timeout') && 
      (reason.includes('BatchSpanProcessor') || 
       stack.includes('BatchSpanProcessor') ||
       stack.includes('zone.js'))
    );
    
    if (isSignOzTimeout) {
      if (enableConsoleLog) {
        console.warn('[SignOz] Span export timeout (this is usually safe to ignore - check if SignOz endpoint is reachable)');
      }
      event.preventDefault(); // Prevent the error from being logged
      return;
    }
    
    const tracer = trace.getTracer('error-tracker');
    const span = tracer.startSpan('Unhandled Promise Rejection');
    
    span.setAttribute('error.type', 'unhandled_rejection');
    span.setAttribute('error.reason', String(event.reason));
    
    // Add page context
    span.setAttribute('page.url', window.location.href);
    span.setAttribute('page.pathname', window.location.pathname);
    span.setAttribute('page.search', window.location.search);
    span.setAttribute('page.hash', window.location.hash);
    
    if (event.reason instanceof Error) {
      span.recordException(event.reason);
      span.setAttribute('error.message', event.reason.message);
      span.setAttribute('error.stack', event.reason.stack || '');
      span.setAttribute('error.name', event.reason.name || 'Error');
    }
    
    span.setStatus({ code: SpanStatusCode.ERROR, message: String(event.reason) });
    
    if (enableConsoleLog) {
      console.error('[SignOz] Unhandled Promise Rejection:', event.reason, 'on page', window.location.pathname);
    }
    
    span.end();
  });
  
  // Track Zone.js errors (for Angular/React with zone.js)
  if (typeof Zone !== 'undefined' && Zone.current) {
    const currentZone = Zone.current as any;
    const originalHandleError = currentZone.handleError;
    if (originalHandleError) {
      currentZone.handleError = function(error: Error) {
        // Skip SignOz internal timeout errors
        const errorMessage = error.message || String(error);
        const errorStack = error.stack || '';
        const isSignOzTimeout = (
          errorMessage.includes('Timeout') && 
          (errorStack.includes('BatchSpanProcessor') || errorStack.includes('zone.js'))
        );
        
        if (isSignOzTimeout) {
          if (enableConsoleLog) {
            console.warn('[SignOz] Zone.js caught span export timeout (safe to ignore)');
          }
          return; // Don't track this error
        }
        
        const tracer = trace.getTracer('error-tracker');
        const span = tracer.startSpan('Zone.js Error');
        
        span.setAttribute('error.type', 'zone_error');
        span.setAttribute('error.message', error.message || String(error));
        span.setAttribute('error.name', error.name || 'Error');
        
        // Add page context
        span.setAttribute('page.url', window.location.href);
        span.setAttribute('page.pathname', window.location.pathname);
        span.setAttribute('page.search', window.location.search);
        span.setAttribute('page.hash', window.location.hash);
        
        if (error.stack) {
          span.setAttribute('error.stack', error.stack);
        }
        
        span.recordException(error);
        span.setStatus({ code: SpanStatusCode.ERROR, message: error.message || String(error) });
        
        if (enableConsoleLog) {
          console.error('[SignOz] Zone.js Error:', error, 'on page', window.location.pathname);
        }
        
        span.end();
        
        // Call original handler
        return originalHandleError.call(currentZone, error);
      };
    }
  }
  
  // Override console.error to catch React errors
  const originalConsoleError = console.error;
  console.error = function(...args: any[]) {
    // Check if this is a React error
    const errorMessage = args.map(arg => String(arg)).join(' ');
    if (errorMessage.includes('React') || errorMessage.includes('TypeError') || errorMessage.includes('Uncaught')) {
      const tracer = trace.getTracer('error-tracker');
      const span = tracer.startSpan('Console Error');
      
      span.setAttribute('error.type', 'console_error');
      span.setAttribute('error.message', errorMessage);
      
      // Add page context
      span.setAttribute('page.url', window.location.href);
      span.setAttribute('page.pathname', window.location.pathname);
      span.setAttribute('page.search', window.location.search);
      span.setAttribute('page.hash', window.location.hash);
      
      // Try to extract stack trace
      const errorObj = args.find(arg => arg instanceof Error);
      if (errorObj) {
        span.recordException(errorObj);
        span.setAttribute('error.stack', errorObj.stack || '');
        span.setAttribute('error.name', errorObj.name || 'Error');
      }
      
      span.setStatus({ code: SpanStatusCode.ERROR, message: errorMessage });
      span.end();
    }
    
    // Call original console.error
    return originalConsoleError.apply(console, args);
  };
}

// Fungsi untuk menambahkan navigation tracking
function addNavigationTracking(enableConsoleLog: boolean = false) {
  let previousUrl = window.location.href;
  
  // Track initial page load
  const tracer = trace.getTracer('navigation-tracker');
  const initialSpan = tracer.startSpan('Page Load');
  initialSpan.setAttribute('navigation.type', 'initial');
  initialSpan.setAttribute('navigation.url', window.location.href);
  initialSpan.setAttribute('navigation.pathname', window.location.pathname);
  initialSpan.setAttribute('navigation.search', window.location.search);
  initialSpan.setAttribute('navigation.hash', window.location.hash);
  
  if (enableConsoleLog) {
    console.log('[SignOz] Page Load:', {
      url: window.location.href,
      pathname: window.location.pathname
    });
  }
  
  initialSpan.end();
  
  // Track popstate (browser back/forward)
  window.addEventListener('popstate', () => {
    const span = tracer.startSpan('Navigation');
    span.setAttribute('navigation.type', 'popstate');
    span.setAttribute('navigation.from', previousUrl);
    span.setAttribute('navigation.to', window.location.href);
    span.setAttribute('navigation.pathname', window.location.pathname);
    span.setAttribute('navigation.search', window.location.search);
    span.setAttribute('navigation.hash', window.location.hash);
    
    if (enableConsoleLog) {
      console.log('[SignOz] Navigation (popstate):', {
        from: previousUrl,
        to: window.location.href,
        pathname: window.location.pathname
      });
    }
    
    previousUrl = window.location.href;
    span.end();
  });
  
  // Track pushState and replaceState (SPA navigation)
  const originalPushState = history.pushState;
  history.pushState = function(...args) {
    const span = tracer.startSpan('Navigation');
    span.setAttribute('navigation.type', 'pushState');
    span.setAttribute('navigation.from', previousUrl);
    
    const result = originalPushState.apply(this, args);
    
    span.setAttribute('navigation.to', window.location.href);
    span.setAttribute('navigation.pathname', window.location.pathname);
    span.setAttribute('navigation.search', window.location.search);
    span.setAttribute('navigation.hash', window.location.hash);
    
    if (enableConsoleLog) {
      console.log('[SignOz] Navigation (pushState):', {
        from: previousUrl,
        to: window.location.href,
        pathname: window.location.pathname
      });
    }
    
    previousUrl = window.location.href;
    span.end();
    
    return result;
  };
  
  const originalReplaceState = history.replaceState;
  history.replaceState = function(...args) {
    const span = tracer.startSpan('Navigation');
    span.setAttribute('navigation.type', 'replaceState');
    span.setAttribute('navigation.from', previousUrl);
    
    const result = originalReplaceState.apply(this, args);
    
    span.setAttribute('navigation.to', window.location.href);
    span.setAttribute('navigation.pathname', window.location.pathname);
    span.setAttribute('navigation.search', window.location.search);
    span.setAttribute('navigation.hash', window.location.hash);
    
    if (enableConsoleLog) {
      console.log('[SignOz] Navigation (replaceState):', {
        from: previousUrl,
        to: window.location.href,
        pathname: window.location.pathname
      });
    }
    
    previousUrl = window.location.href;
    span.end();
    
    return result;
  };
}

// Fungsi untuk menambahkan WebSocket logging
function addWebSocketLogging(config: {
  enableWebSocketLogging: boolean;
  logWebSocketMessages: boolean;
  maxBodyLogSize: number;
  enableConsoleLog: boolean;
}) {
  if (!config.enableWebSocketLogging) return;

  const OriginalWebSocket = window.WebSocket;
  
  window.WebSocket = function(url: string | URL, protocols?: string | string[]) {
    const ws = new OriginalWebSocket(url, protocols);
    const wsUrl = typeof url === 'string' ? url : url.toString();
    
    // Capture current page info
    const pageUrl = window.location.href;
    const pagePath = window.location.pathname;
    
    const tracer = trace.getTracer('websocket-logger');
    let connectionSpan = tracer.startSpan('WebSocket Connection');
    const connectionStartTime = performance.now();
    
    connectionSpan.setAttribute('websocket.url', wsUrl);
    connectionSpan.setAttribute('websocket.protocols', Array.isArray(protocols) ? protocols.join(',') : (protocols || 'none'));
    connectionSpan.setAttribute('page.url', pageUrl);
    connectionSpan.setAttribute('page.pathname', pagePath);
    
    // Track connection open
    const originalOnOpen = ws.onopen;
    ws.addEventListener('open', function(event) {
      const duration = performance.now() - connectionStartTime;
      connectionSpan.setAttribute('websocket.state', 'open');
      connectionSpan.setAttribute('duration_ms', Math.round(duration));
      connectionSpan.setStatus({ code: SpanStatusCode.OK });
      
      if (config.enableConsoleLog) {
        console.log(`[SignOz] WebSocket Connected to ${wsUrl} from page ${pagePath} [${Math.round(duration)}ms]`);
      }
      
      connectionSpan.end();
      
      if (originalOnOpen) {
        originalOnOpen.call(ws, event);
      }
    });
    
    // Track messages sent
    const originalSend = ws.send;
    ws.send = function(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
      if (config.logWebSocketMessages) {
        // Skip logging if data is just a number
        let shouldLog = true;
        if (typeof data === 'string') {
          // Check if string is just a number
          if (/^\d+$/.test(data.trim())) {
            shouldLog = false;
          }
        } else if (typeof data === 'number') {
          shouldLog = false;
        }
        
        if (!shouldLog) {
          return originalSend.call(ws, data);
        }
        
        const messageSpan = tracer.startSpan('WebSocket Send');
        const sendStartTime = performance.now();
        
        messageSpan.setAttribute('websocket.url', wsUrl);
        messageSpan.setAttribute('websocket.direction', 'send');
        messageSpan.setAttribute('page.url', pageUrl);
        messageSpan.setAttribute('page.pathname', pagePath);
        
        // Log message data
        if (typeof data === 'string') {
          const truncatedData = truncateBody(data, config.maxBodyLogSize);
          messageSpan.setAttribute('websocket.message', truncatedData);
          messageSpan.setAttribute('http.request.body', truncatedData); // Add for consistency with HTTP requests
          
          // Try to parse JSON and extract message if exists
          try {
            const jsonData = JSON.parse(data);
            if (jsonData.message) {
              messageSpan.setAttribute('websocket.message.text', jsonData.message);
            }
            if (jsonData.type || jsonData.event) {
              messageSpan.setAttribute('websocket.message.type', jsonData.type || jsonData.event);
            }
          } catch (e) {
            // Not JSON, ignore
          }
          
          if (config.enableConsoleLog) {
            console.log(`[SignOz] WebSocket Send to ${wsUrl} from page ${pagePath}:`, truncatedData);
          }
        } else if (typeof data === 'number' || typeof data === 'boolean') {
          // Handle primitive types
          const dataStr = String(data);
          messageSpan.setAttribute('websocket.message', dataStr);
          messageSpan.setAttribute('http.request.body', dataStr);
          messageSpan.setAttribute('websocket.message.type', typeof data);
          
          if (config.enableConsoleLog) {
            console.log(`[SignOz] WebSocket Send to ${wsUrl} from page ${pagePath}:`, dataStr);
          }
        } else {
          messageSpan.setAttribute('websocket.message', '[Binary Data]');
          messageSpan.setAttribute('websocket.message.type', 'binary');
          
          if (config.enableConsoleLog) {
            console.log(`[SignOz] WebSocket Send to ${wsUrl} from page ${pagePath}: [Binary Data]`);
          }
        }
        
        const duration = performance.now() - sendStartTime;
        messageSpan.setAttribute('duration_ms', Math.round(duration));
        messageSpan.setStatus({ code: SpanStatusCode.OK });
        messageSpan.end();
      }
      
      return originalSend.call(ws, data);
    };
    
    // Track messages received
    const originalOnMessage = ws.onmessage;
    ws.addEventListener('message', function(event) {
      if (config.logWebSocketMessages) {
        // Skip logging if data is just a number
        let shouldLog = true;
        if (typeof event.data === 'string') {
          // Check if string is just a number
          if (/^\d+$/.test(event.data.trim())) {
            shouldLog = false;
          }
        } else if (typeof event.data === 'number') {
          shouldLog = false;
        }
        
        if (!shouldLog) {
          if (originalOnMessage) {
            originalOnMessage.call(ws, event);
          }
          return;
        }
        
        const messageSpan = tracer.startSpan('WebSocket Receive');
        
        messageSpan.setAttribute('websocket.url', wsUrl);
        messageSpan.setAttribute('websocket.direction', 'receive');
        messageSpan.setAttribute('page.url', pageUrl);
        messageSpan.setAttribute('page.pathname', pagePath);
        
        // Log message data
        if (typeof event.data === 'string') {
          const truncatedData = truncateBody(event.data, config.maxBodyLogSize);
          messageSpan.setAttribute('websocket.message', truncatedData);
          messageSpan.setAttribute('response.data', truncatedData); // Add response.data for consistency
          
          // Try to parse JSON and extract message if exists
          try {
            const jsonData = JSON.parse(event.data);
            if (jsonData.message) {
              messageSpan.setAttribute('websocket.message.text', jsonData.message);
              messageSpan.setAttribute('response.message', jsonData.message); // Add response.message for consistency
            }
            if (jsonData.type || jsonData.event) {
              messageSpan.setAttribute('websocket.message.type', jsonData.type || jsonData.event);
            }
          } catch (e) {
            // Not JSON, ignore
          }
          
          if (config.enableConsoleLog) {
            console.log(`[SignOz] WebSocket Receive from ${wsUrl} on page ${pagePath}:`, truncatedData);
          }
        } else if (typeof event.data === 'number' || typeof event.data === 'boolean') {
          // Handle primitive types
          const dataStr = String(event.data);
          messageSpan.setAttribute('websocket.message', dataStr);
          messageSpan.setAttribute('response.data', dataStr);
          messageSpan.setAttribute('websocket.message.type', typeof event.data);
          
          if (config.enableConsoleLog) {
            console.log(`[SignOz] WebSocket Receive from ${wsUrl} on page ${pagePath}:`, dataStr);
          }
        } else {
          messageSpan.setAttribute('websocket.message', '[Binary Data]');
          messageSpan.setAttribute('websocket.message.type', 'binary');
          messageSpan.setAttribute('response.data', '[Binary Data]'); // Add response.data for consistency
          
          if (config.enableConsoleLog) {
            console.log(`[SignOz] WebSocket Receive from ${wsUrl} on page ${pagePath}: [Binary Data]`);
          }
        }
        
        messageSpan.setStatus({ code: SpanStatusCode.OK });
        messageSpan.end();
      }
      
      if (originalOnMessage) {
        originalOnMessage.call(ws, event);
      }
    });
    
    // Track connection errors
    const originalOnError = ws.onerror;
    ws.addEventListener('error', function(event) {
      const errorSpan = tracer.startSpan('WebSocket Error');
      
      errorSpan.setAttribute('websocket.url', wsUrl);
      errorSpan.setAttribute('websocket.state', 'error');
      errorSpan.setAttribute('page.url', pageUrl);
      errorSpan.setAttribute('page.pathname', pagePath);
      
      errorSpan.recordException(new Error('WebSocket connection error'));
      errorSpan.setStatus({ code: SpanStatusCode.ERROR, message: 'WebSocket connection error' });
      
      if (config.enableConsoleLog) {
        console.error(`[SignOz] WebSocket Error for ${wsUrl} on page ${pagePath}`);
      }
      
      errorSpan.end();
      
      if (originalOnError) {
        originalOnError.call(ws, event);
      }
    });
    
    // Track connection close
    const originalOnClose = ws.onclose;
    ws.addEventListener('close', function(event) {
      const closeSpan = tracer.startSpan('WebSocket Close');
      
      closeSpan.setAttribute('websocket.url', wsUrl);
      closeSpan.setAttribute('websocket.state', 'closed');
      closeSpan.setAttribute('websocket.close.code', event.code);
      closeSpan.setAttribute('websocket.close.reason', event.reason || 'No reason provided');
      closeSpan.setAttribute('websocket.close.wasClean', event.wasClean);
      closeSpan.setAttribute('page.url', pageUrl);
      closeSpan.setAttribute('page.pathname', pagePath);
      
      if (config.enableConsoleLog) {
        console.log(`[SignOz] WebSocket Closed ${wsUrl} on page ${pagePath} - Code: ${event.code}, Reason: ${event.reason || 'No reason'}, Clean: ${event.wasClean}`);
      }
      
      closeSpan.setStatus({ code: SpanStatusCode.OK });
      closeSpan.end();
      
      if (originalOnClose) {
        originalOnClose.call(ws, event);
      }
    });
    
    return ws;
  } as any;
}

/**
 * Inisialisasi SignOz tracing untuk aplikasi React
 * @param config - Konfigurasi SignOz (opsional, akan menggunakan environment variables jika tidak disediakan)
 */
export function initializeSignOzTracing(config?: SignOzConfig): void {
  try {
    // Gabungkan konfigurasi dari parameter, process.env, dan window
    const effectiveConfig = {
      serviceName: config?.serviceName || getConfigValue('REACT_APP_SIGNOZ_SERVICE_NAME'),
      serviceVersion: config?.serviceVersion || getConfigValue('REACT_APP_SIGNOZ_SERVICE_VERSION'),
      environment: config?.environment || getConfigValue('REACT_APP_SIGNOZ_ENV'),
      serviceNamespace: config?.serviceNamespace || getConfigValue('REACT_APP_SIGNOZ_SERVICE_NAMESPACE'),
      url: config?.url || getConfigValue('REACT_APP_SIGNOZ_URL'),
      headers: config?.headers || {},
      traceSampleRate: (() => {
        const rate = config?.traceSampleRate || parseFloat(String(getConfigValue('REACT_APP_SIGNOZ_TRACE_SAMPLE_RATE'))) || 1.0;
        return Math.max(0, Math.min(1, rate)); // Ensure rate is between 0 and 1
      })(),
      batchSpanProcessorConfig: config?.batchSpanProcessorConfig || {
        maxQueueSize: 100,
        scheduledDelayMillis: 5000,
        exportTimeoutMillis: 60000, // Increased to 60 seconds
        maxExportBatchSize: 50 // Smaller batches to avoid timeouts
      },
      allowedOrigins: config?.allowedOrigins || parseAllowedOrigins(getConfigValue('REACT_APP_SIGNOZ_ALLOWED_ORIGINS')),
      enableRequestLogging: config?.enableRequestLogging !== undefined ? config.enableRequestLogging : (getConfigValue('REACT_APP_SIGNOZ_ENABLE_REQUEST_LOGGING') === 'true' || true),
      logRequestBody: config?.logRequestBody !== undefined ? config.logRequestBody : (getConfigValue('REACT_APP_SIGNOZ_LOG_REQUEST_BODY') === 'true' || true),
      logResponseBody: config?.logResponseBody !== undefined ? config.logResponseBody : (getConfigValue('REACT_APP_SIGNOZ_LOG_RESPONSE_BODY') === 'true' || true),
      maxBodyLogSize: config?.maxBodyLogSize || parseInt(getConfigValue('REACT_APP_SIGNOZ_MAX_BODY_LOG_SIZE') || '10000') || 10000,
      enableDocumentLoad: config?.enableDocumentLoad !== undefined ? config.enableDocumentLoad : (getConfigValue('REACT_APP_SIGNOZ_ENABLE_DOCUMENT_LOAD') === 'true'),
      enableErrorTracking: config?.enableErrorTracking !== undefined ? config.enableErrorTracking : (getConfigValue('REACT_APP_SIGNOZ_ENABLE_ERROR_TRACKING') !== 'false'),
      enableNavigationTracking: config?.enableNavigationTracking !== undefined ? config.enableNavigationTracking : (getConfigValue('REACT_APP_SIGNOZ_ENABLE_NAVIGATION_TRACKING') === 'true'),
      enableConsoleLog: config?.enableConsoleLog !== undefined ? config.enableConsoleLog : (getConfigValue('REACT_APP_SIGNOZ_ENABLE_CONSOLE_LOG') === 'true'),
      enableWebSocketLogging: config?.enableWebSocketLogging !== undefined ? config.enableWebSocketLogging : (getConfigValue('REACT_APP_SIGNOZ_ENABLE_WEBSOCKET_LOGGING') === 'true'),
      logWebSocketMessages: config?.logWebSocketMessages !== undefined ? config.logWebSocketMessages : (getConfigValue('REACT_APP_SIGNOZ_LOG_WEBSOCKET_MESSAGES') === 'true')
    };

    // Validasi konfigurasi
    const { isValid, missingFields } = validateConfig(effectiveConfig);

    if (!isValid) {
      console.warn(`SignOz: Konfigurasi tidak lengkap. Field yang hilang: ${missingFields.join(', ')}`);
      console.warn('SignOz: Tracing dinonaktifkan karena konfigurasi tidak lengkap');
      return;
    }

    // Define resource and service attributes
    const resource = new Resource({
      'service.name': effectiveConfig.serviceName,
      'service.version': effectiveConfig.serviceVersion,
      'deployment.environment': effectiveConfig.environment,
      'service.namespace': effectiveConfig.serviceNamespace,
    });

    // Set up the OTLP trace exporter
    const exporter = new OTLPTraceExporter({
      url: effectiveConfig.url,
      headers: effectiveConfig.headers,
      timeoutMillis: 60000 // Set exporter timeout to 60 seconds
    });

    // Set up the span processor with configuration
    const processor = new BatchSpanProcessor(exporter, {
      maxQueueSize: effectiveConfig.batchSpanProcessorConfig.maxQueueSize,
      scheduledDelayMillis: effectiveConfig.batchSpanProcessorConfig.scheduledDelayMillis,
      exportTimeoutMillis: effectiveConfig.batchSpanProcessorConfig.exportTimeoutMillis
    });

    // Create and configure the WebTracerProvider
    const provider = new WebTracerProvider({
      resource: resource,
      sampler: new ParentBasedSampler({
        root: new TraceIdRatioBasedSampler(effectiveConfig.traceSampleRate)
      })
    });

    // Add span processor to the provider
    provider.addSpanProcessor(processor);

    // Register the tracer provider with the context manager
    provider.register({
      contextManager: new ZoneContextManager(),
    });

    // Set up automatic instrumentation for web APIs
    registerInstrumentations({
      instrumentations: [
        getWebAutoInstrumentations({
          // Nonaktifkan XMLHttpRequest auto-instrumentation (kita pakai custom logging)
          '@opentelemetry/instrumentation-xml-http-request': {
            enabled: false,
          },
          // Nonaktifkan Fetch auto-instrumentation (kita pakai custom logging)
          '@opentelemetry/instrumentation-fetch': {
            enabled: false,
          },
          // Nonaktifkan user interaction instrumentation (click events)
          '@opentelemetry/instrumentation-user-interaction': {
            enabled: false,
          },
          // Document load instrumentation untuk page load performance
          '@opentelemetry/instrumentation-document-load': {
            enabled: effectiveConfig.enableDocumentLoad,
          },
        }),
      ],
    });

    // Tambahkan custom logging untuk request/response
    if (effectiveConfig.enableRequestLogging) {
      addFetchLogging({
        enableRequestLogging: effectiveConfig.enableRequestLogging,
        logRequestBody: effectiveConfig.logRequestBody,
        logResponseBody: effectiveConfig.logResponseBody,
        maxBodyLogSize: effectiveConfig.maxBodyLogSize,
        enableConsoleLog: effectiveConfig.enableConsoleLog
      });
      
      addXHRLogging({
        enableRequestLogging: effectiveConfig.enableRequestLogging,
        logRequestBody: effectiveConfig.logRequestBody,
        logResponseBody: effectiveConfig.logResponseBody,
        maxBodyLogSize: effectiveConfig.maxBodyLogSize,
        enableConsoleLog: effectiveConfig.enableConsoleLog
      });
    }

    // Tambahkan error tracking
    if (effectiveConfig.enableErrorTracking) {
      addErrorTracking(effectiveConfig.enableConsoleLog);
    }

    // Tambahkan navigation tracking
    if (effectiveConfig.enableNavigationTracking) {
      addNavigationTracking(effectiveConfig.enableConsoleLog);
    }

    // Tambahkan WebSocket logging
    if (effectiveConfig.enableWebSocketLogging) {
      addWebSocketLogging({
        enableWebSocketLogging: effectiveConfig.enableWebSocketLogging,
        logWebSocketMessages: effectiveConfig.logWebSocketMessages,
        maxBodyLogSize: effectiveConfig.maxBodyLogSize,
        enableConsoleLog: effectiveConfig.enableConsoleLog
      });
    }

    console.log('SignOz: Konfigurasi tracing:', {
      serviceName: effectiveConfig.serviceName,
      environment: effectiveConfig.environment,
      allowedOrigins: effectiveConfig.allowedOrigins,
      traceSampleRate: effectiveConfig.traceSampleRate,
      enableRequestLogging: effectiveConfig.enableRequestLogging,
      logRequestBody: effectiveConfig.logRequestBody,
      logResponseBody: effectiveConfig.logResponseBody,
      maxBodyLogSize: effectiveConfig.maxBodyLogSize,
      enableDocumentLoad: effectiveConfig.enableDocumentLoad,
      enableErrorTracking: effectiveConfig.enableErrorTracking,
      enableNavigationTracking: effectiveConfig.enableNavigationTracking,
      enableWebSocketLogging: effectiveConfig.enableWebSocketLogging,
      logWebSocketMessages: effectiveConfig.logWebSocketMessages
    });
    console.log('SignOz: Tracing berhasil diinisialisasi');
  } catch (e: unknown) {
    const error = e as Error;
    console.error('SignOz: Gagal menginisialisasi tracing:', error);
    console.error('SignOz: Detail error:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
  }
}

export default initializeSignOzTracing;